import { ErrorLink } from '@apollo/client/link/error'
import { createClient } from 'graphql-ws'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { SetContextLink } from '@apollo/client/link/context'
import { getMainDefinition } from '@apollo/client/utilities'
import { CombinedGraphQLErrors } from '@apollo/client/errors'
import { getAccessToken, clearAccessToken } from 'lib/auth'
import { ApolloClient, ApolloLink, InMemoryCache, HttpLink } from '@apollo/client'
// types
import { AuthErrorCode } from 'types/auth/helpers'
// store
import { store } from 'store/store'
import { LOGOUT_ACTION } from 'store/types'

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_GQL_API_URL,
  credentials: 'include'
})

const cache = new InMemoryCache({ possibleTypes: { UserOrPosition: ['User', 'Position'] } })

const wsLink = new GraphQLWsLink(
  createClient({
    url: import.meta.env.VITE_GQL_WS_URL,
    lazy: true,
    connectionParams: () => {
      const accessToken = getAccessToken()

      return {
        headers: {
          Authorization: accessToken ? `Bearer ${accessToken}` : ''
        }
      }
    }
  })
)

const authLink = new SetContextLink(({ headers }) => {
  const accessToken = getAccessToken()

  return {
    headers: {
      ...headers,
      ...(accessToken && { Authorization: `Bearer ${accessToken}` })
    }
  }
})

let isRedirectingToLogin = false

const errorLink = new ErrorLink(({ error }) => {
  if (!CombinedGraphQLErrors.is(error)) return

  for (const err of error.errors) {
    const code = err.extensions?.code as string

    switch (code) {
      case AuthErrorCode.UNAUTHENTICATED:
      case AuthErrorCode.SESSION_TOKEN_REUSE:
      case AuthErrorCode.SESSION_EXPIRED:
      case AuthErrorCode.SESSION_REVOKED:
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/public/')) {
          break
        }
        clearAccessToken()
        store.dispatch({ type: LOGOUT_ACTION })
        if (typeof window !== 'undefined' && !isRedirectingToLogin && !window.location.pathname.startsWith('/login')) {
          isRedirectingToLogin = true
          window.location.href = '/login'
        }
        break

      case AuthErrorCode.TWO_FACTOR_REQUIRED:
        // SKEFTO_ADMIN without 2FA — let the component handle the error message
        break

      case AuthErrorCode.TOKEN_EXPIRED:
        // Handled by SessionProvider auto-refresh; no action needed here
        break
    }
  }
})

const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  ApolloLink.from([errorLink, authLink, httpLink])
)

const createApolloClient = () => {
  return new ApolloClient({
    cache,
    link: splitLink
  })
}

const createPublicApolloClient = () => {
  // Separate cache instance to avoid polluting the authenticated client's
  // normalized cache with unauthenticated (login/public form) responses.
  const publicCache = new InMemoryCache({ possibleTypes: { UserOrPosition: ['User', 'Position'] } })

  return new ApolloClient({
    cache: publicCache,
    link: ApolloLink.from([httpLink]),
    defaultOptions: {
      watchQuery: {
        notifyOnNetworkStatusChange: false
      }
    }
  })
}

export const client = createApolloClient()
export const publicClient = createPublicApolloClient()
